import styles from './ChronoFileList.module.scss';

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useLocation, useNavigate, useWidgetsDataBag } from '@work4all/data';
import { useEntityEvents } from '@work4all/data/lib/entity-events/use-entity-events';
import { usePermissions } from '@work4all/data/lib/hooks/use-permissions';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { FileType } from '@work4all/models/lib/File';
import { entityTypeLookup } from '@work4all/models/lib/table-schema/entityTypeLookup';
import { ITableSchema } from '@work4all/models/lib/table-schema/table-schema';

import { mutationEntityToViewEntity } from '@work4all/utils';

import { EntityTableLayoutV8 } from '../../../../../components/data-tables/table/EntityTableLayoutV8';
import { SelectedItemContext } from '../card-widgets/selected-item-context';

import { IAggregatedList, useChronoFileList } from './hooks/useChronoFileList';

export interface ChronoFileListProps extends IAggregatedList {
  id?: string | number;
  type?: FileType;
  schema: ITableSchema<never>;
}

export function ChronoFileList(props: ChronoFileListProps) {
  const layout = 'table';

  const { table, cardConfig, data, fetchMore, refetch, resetColumns, pending } =
    useChronoFileList({
      layout,
      ...props,
      onRowsSelected: (rows) => {
        if (rows.length === 1) {
          setSelected(rows[0].id);
        }
      },
    });

  const { canEdit } = usePermissions();

  const { setItem } = useContext(SelectedItemContext);
  const { widgetsDefinitions } = useWidgetsDataBag();

  const location = useLocation();
  const navigate = useNavigate();

  const findEntity = useCallback(
    (id: string) => {
      const dataItem = data.find(
        (x) => x.primaryKey === parseInt(id) || x.primaryKey === id
      );
      if (!dataItem?.fileItem?.__typename) return;
      const mapping = Object.entries(entityTypeLookup).find(
        (x) => x[1] === dataItem.fileItem.__typename
      );
      return mapping;
    },
    [data]
  );

  const [selected, setSelected] = useState<string>();

  useEffect(() => {
    if (!selected) return;
    const mapping = findEntity(selected);
    if (!mapping) return;
    if (!widgetsDefinitions?.definition) return;
    const widget = widgetsDefinitions.definition
      .flatMap((x) => x.widgets)
      .find((x) =>
        x.config.entities.some((x) => x.entityTypeName === mapping[1])
      );
    if (!widget) return;

    setItem({
      type: mapping[0] as Entities,
      id: Number(selected) || selected,
      widgetId: widget.id,
    });
  }, [selected, widgetsDefinitions, findEntity, setItem]);

  const editAction = useMemo(() => {
    return {
      handler: (id: string) => {
        /**
         * todo differentiate erp and erp_lightcase here too
         * 1. edit can be always clicked as the contextmenu is for the table not one row currently
         * 2. differentiate between double click and single click to open normal preview, or preview in fullscreenMode
         */
        const mapping = findEntity(id);
        const entityType = mutationEntityToViewEntity(mapping?.[0] as Entities);
        if (!entityType || !canEdit({ entity: entityType, record: null }))
          return;
        const href = `${location.pathname}/details/${entityType}/${id}`;
        navigate(href);
      },
      getHref: (id: string) => {
        const mapping = findEntity(id);
        const entityType = mutationEntityToViewEntity(mapping?.[0] as Entities);
        if (!entityType || !canEdit({ entity: entityType, record: null }))
          return;
        return `${location.pathname}/details/${mapping[0]}/${id}`;
      },
    };
  }, [findEntity, canEdit, location.pathname, navigate]);

  useEntityEvents(() => {
    refetch();
  });

  return (
    <div className={styles.main}>
      <EntityTableLayoutV8
        table={table}
        actions={{
          edit: editAction,
          resetColumns: resetColumns,
        }}
        pending={pending}
        layout={layout}
        cardConfig={cardConfig}
        fetchMore={fetchMore}
        hideToolbar={true}
        classes={{ root: styles.root }}
      />
    </div>
  );
}
